import {
  usePeopleAnalyticsDashboards,
  usePeopleAnalyticsWidgetData,
} from "@/queries/reporting/use-people-analytics.query.ts";
import { CompensationRollupType } from "@/types/analytics/analytics.employee.types.ts";
import {
  ApiPeopleAnalyticsEmployeeFilter,
  ApiPeopleAnalyticsPayrollAllocation,
  ApiPeopleAnalyticsWidgetDataInput,
} from "@/types/reporting/people-analytics.types.ts";
import {
  ApiWidgetDimension,
  ApiWidgetPreviewInput,
  ApiWidgetType,
} from "@/types/reporting/reporting.types.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Системный дашборд salary
const SALARY_DASHBOARD_KEY = "salary";

export function useSalaryDashboard() {
  const dashboards = usePeopleAnalyticsDashboards();
  return {
    dashboard: dashboards.data?.find((dashboard) => dashboard.system_key === SALARY_DASHBOARD_KEY),
    isLoading: dashboards.isLoading,
  };
}

function resolveSystemKey(input: ApiWidgetPreviewInput): string {
  const metric = input.query.metrics[0];
  const dimension = input.query.dimensions?.[0];
  if (input.widget_type === ApiWidgetType.kpi) return `salary.kpi.${metric}`;
  if (dimension === ApiWidgetDimension.salary_last_updated) {
    return `salary.by.last_updated.${metric}`;
  }
  if (dimension) return `salary.by.${dimension}.${metric}`;
  return `salary.over_time.${metric}`;
}

export function useSalaryWidget(
  input: ApiWidgetPreviewInput,
  enabled: boolean,
  allocation?: ApiPeopleAnalyticsPayrollAllocation
) {
  const { t } = useTranslation();
  const salaryDashboard = useSalaryDashboard();

  const systemKey = resolveSystemKey(input);
  const dashboardWidget = salaryDashboard.dashboard?.widgets.find(
    (item) => item.widget.system_key === systemKey
  )?.widget;
  const widgetId = dashboardWidget?.id ?? null;

  const dataInput = useMemo<ApiPeopleAnalyticsWidgetDataInput>(() => {
    const filter: ApiPeopleAnalyticsEmployeeFilter = {};
    (input.query.filters ?? []).forEach((item) => {
      filter[item.field] = item.ids;
    });
    return {
      date_from: input.period.date_from as Date,
      date_to: input.period.date_to as Date,
      date_grain: input.query.date_grain ?? null,
      filter,
      compensation: {
        rollup_type: input.query.compensation?.rollup_type ?? CompensationRollupType.gross,
        currency: input.query.compensation?.currency ?? null,
        allocation,
      },
    };
  }, [allocation, input]);
  const analytics = usePeopleAnalyticsWidgetData(widgetId, dataInput, enabled);

  const data = useMemo(
    () =>
      analytics.data && {
        ...analytics.data,
        series: analytics.data.series.map((series) => ({
          ...series,
          points: series.points.map((point) =>
            point.label ? point : { ...point, label: t("reporting:people_analytics_group_empty") }
          ),
        })),
      },
    [analytics.data, t]
  );

  return {
    data,
    isLoading: salaryDashboard.isLoading || analytics.isLoading,
    isError: analytics.isError,
    bucketSort: dashboardWidget?.visualization.bucket_sort,
  };
}
